import { Card } from "@rumbledirect/ui";
import { Col, Flex, message, Row } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deleteCompanyById,
  fetchCompanyById,
} from "../../../lib/api/resources/companies";
import { CompanyDetailCard } from "./components/CompanyDetailCard";
import { CompanyServicePartnersTable } from "./components/CompanyServicePartnersTable";
import { ServicePartner } from "@rumble-it/common-lib/types/models/ServicePartner.interface";
import { Company } from "@rumble-it/common-lib/types/models/Company.interface";

export type CompanyDetailValues = Omit<Company, "servicePartners"> & {
  servicePartners: { value: string | null; fullName: string | null }[];
};

export const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  if (!id) return null;

  const companyQuery = useQuery({
    queryKey: ["company", id],
    queryFn: async (): Promise<CompanyDetailValues> => {
      if (!id) {
        throw new Error("No company ID provided");
      }
      const company = await fetchCompanyById(id);
      const servicePartners =
        company?.servicePartners?.map(
          (servicePartner: Partial<ServicePartner>) => ({
            value: servicePartner?.id,
            fullName: servicePartner?.fullName,
          }),
        ) || [];
      company.servicePartners = servicePartners;
      return company as CompanyDetailValues;
    },
  });

  const deleteCompanyMutation = useMutation({
    mutationFn: () => deleteCompanyById(id),
    onSuccess: () => {
      navigate(`/companies`);
      message.success("Bedrijf succesvol verwijderd");
    },
    onError: () => message.error("Verwijderen van het bedrijf is mislukt"),
  });

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Flex vertical gap={16}>
            <CompanyDetailCard
              deleteMutation={deleteCompanyMutation}
              company={companyQuery.data}
              isLoading={companyQuery.isFetching}
            />
          </Flex>
        </Col>
        <Col span={12}>
          <Card
            loading={companyQuery.isFetching}
            title="Gekoppelde herstellers"
          >
            <CompanyServicePartnersTable
              servicePartners={companyQuery.data?.servicePartners}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};
